import React, { useState, useEffect, useCallback } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import Modal from "../../../components/modal/Modal";
import { MaintenanceSchedule } from "../../../types/api";
import { getAssetInventory } from "../../../service/api";
import { useOrgUsersStore } from "../../../store/store";
import { usePreventiveMaintenanceStore } from "../../../store/utilitySystemsStore";
interface MaintenanceScheduleFormProps {
  isOpen: boolean;
  onClose: () => void;
  schedule?: MaintenanceSchedule | null;
  mode: "create" | "edit" | "view";
  onSuccess?: () => void;
}

const MaintenanceScheduleForm: React.FC<MaintenanceScheduleFormProps> = ({
  isOpen,
  onClose,
  schedule,
  mode,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    assetId: "",
    frequency: "monthly" as "daily" | "weekly" | "monthly" | "quarterly" | "yearly",
    nextDueDate: new Date(),
    technicianId: "",
    status: "Active" as "Active" | "Inactive" | "Suspended",
    description: "",
    priority: "Medium" as "Low" | "Medium" | "High" | "Critical",
  });
  const [assets, setAssets] = useState<any[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();
  const {
    schedulesLoading,
    fetchMaintenanceScheduleById,
    createMaintenanceSchedule: createSchedule,
    updateMaintenanceSchedule: updateSchedule,
  } = usePreventiveMaintenanceStore();

  const isDisabled = mode === "view";
  const isReadOnly = mode === "view";

  const fetchAssets = useCallback(async () => {
    try {
      const response = await getAssetInventory();
      if (response.data?.data) {
        setAssets(Array.isArray(response.data.data) ? response.data.data : []);
      } else if (response.data?.message) {
        setAssets(Array.isArray(response.data.message) ? response.data.message : []);
      }
    } catch (error) {
      console.error("Error fetching asset inventory:", error);
    }
  }, []);

  const loadScheduleData = useCallback(async () => {
    if (!schedule) return;

    try {
      const id = schedule.id || schedule._id;
      if (!id) return;

      const data = await fetchMaintenanceScheduleById(id);
      if (data) {
        setFormData({
          assetId: data.assetId || "",
          frequency: data.frequency || "monthly",
          nextDueDate: data.nextDueDate ? new Date(data.nextDueDate) : new Date(),
          technicianId: data.technicianId || "",
          status: data.status || "Active",
          description: data.description || "",
          priority: data.priority || "Medium",
        });
      } else {
        // Fallback
        setFormData({
          assetId: schedule.assetId || "",
          frequency: schedule.frequency || "monthly",
          nextDueDate: schedule.nextDueDate ? new Date(schedule.nextDueDate) : new Date(),
          technicianId: schedule.technicianId || "",
          status: schedule.status || "Active",
          description: schedule.description || "",
          priority: schedule.priority || "Medium",
        });
      }
    } catch (error) {
      console.error("Error loading schedule:", error);
      toast.error("Failed to load schedule data");
      // Fallback 
      if (schedule) {
        setFormData({
          assetId: schedule.assetId || "",
          frequency: schedule.frequency || "monthly",
          nextDueDate: schedule.nextDueDate ? new Date(schedule.nextDueDate) : new Date(),
          technicianId: schedule.technicianId || "",
          status: schedule.status || "Active",
          description: schedule.description || "",
          priority: schedule.priority || "Medium",
        });
      }
    }
  }, [schedule, fetchMaintenanceScheduleById]);

  useEffect(() => {
    if (!orgUsersData.length) fetchOrgUsers();
    fetchAssets();
  }, [ orgUsersData, fetchAssets]);

  useEffect(() => {
    if (schedule && mode !== "create") {
      loadScheduleData();
    }
  }, [schedule, mode, loadScheduleData]);

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.assetId) {
      newErrors.assetId = "Asset is required";
    }

    if (!formData.frequency) {
      newErrors.frequency = "Frequency is required";
    }

    if (!formData.nextDueDate) {
      newErrors.nextDueDate = "Next Due Date is required";
    } else {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dueDate = new Date(formData.nextDueDate);
      dueDate.setHours(0, 0, 0, 0);

      if (dueDate < today) {
        newErrors.nextDueDate = "Next Due Date must be today or a future date";
      }
    }

    if (!formData.technicianId) {
      newErrors.technicianId = "Technician is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isDisabled) return;

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    try {
      const submitData = {
        assetId: formData.assetId,
        assetName: assets.find((a) => (a._id || a.id) === formData.assetId)?.name || 
                  assets.find((a) => (a._id || a.id) === formData.assetId)?.equipmentName || "",
        frequency: formData.frequency,
        nextDueDate: formData.nextDueDate.toISOString(),
        technicianId: formData.technicianId,
        technicianName: orgUsersData.find((u: any) => (u._id || u.id) === formData.technicianId) 
          ? `${orgUsersData.find((u: any) => (u._id || u.id) === formData.technicianId)?.firstName || ""} ${orgUsersData.find((u: any) => (u._id || u.id) === formData.technicianId)?.lastName || ""}`.trim()
          : "",
        status: formData.status,
        description: formData.description,
        priority: formData.priority,
      };

      if (mode === "create") {
        await createSchedule(submitData);
        toast.success("Maintenance schedule created successfully");
      } else if (mode === "edit" && schedule) {
        const id = schedule.id || schedule._id;
        if (id) {
          await updateSchedule(id, submitData);
          toast.success("Maintenance schedule updated successfully");
        }
      }

      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.message || "Failed to save maintenance schedule");
      console.error("Error saving schedule:", error);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeButton={false}
      modalClass="bg-white w-full max-w-3xl rounded-xl shadow-lg relative max-h-[80vh] flex flex-col"
    >
      <div className="pt-6 px-6 flex-1 overflow-y-auto">
        <h2 className="text-xl font-semibold text-gray-800 border-b pb-2 mb-6">
          {mode === "create"
            ? "Create Maintenance Schedule"
            : mode === "edit"
            ? "Edit Maintenance Schedule"
            : "View Maintenance Schedule"}
        </h2>
        <form onSubmit={handleSubmit} id="maintenanceScheduleForm" className="flex flex-col gap-6 pb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Asset */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Asset <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.assetId}
                onChange={(e) => handleChange("assetId", e.target.value)}
                className={`w-full p-2 border ${
                  errors.assetId ? "border-red-500" : "border-gray-300"
                } rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none`}
                required
                disabled={isDisabled}
                // readOnly={isReadOnly}
              >
                <option value="">Select Asset</option>
                {assets.map((asset) => (
                  <option key={asset._id || asset.id} value={asset._id || asset.id}>
                    {asset.assetName || asset.name || asset.assetCode || asset.code || asset._id || asset.id}
                  </option>
                ))}
              </select>
              {errors.assetId && (
                <p className="text-red-500 text-sm mt-1">{errors.assetId}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Frequency <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.frequency}
                onChange={(e) =>
                  handleChange("frequency", e.target.value as any)
                }
                className={`w-full p-2 border ${
                  errors.frequency ? "border-red-500" : "border-gray-300"
                } rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none`}
                required
                disabled={isDisabled}
                // readOnly={isReadOnly}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </select>
              {errors.frequency && (
                <p className="text-red-500 text-sm mt-1">{errors.frequency}</p>
              )}
            </div>

            {/* Next Due Date */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Next Due Date <span className="text-red-500">*</span>
              </label>
              <DatePicker
                selected={formData.nextDueDate}
                onChange={(date: Date | null) =>
                  handleChange("nextDueDate", date || new Date())
                }
                minDate={new Date()}
                placeholderText="Select date"
                dateFormat="dd/MM/yyyy"
                wrapperClassName="datePicker"
                className={`w-full p-2 border ${
                  errors.nextDueDate ? "border-red-500" : "border-gray-300"
                } rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none`}
                disabled={isDisabled}
                readOnly={isReadOnly}
              />
              {errors.nextDueDate && (
                <p className="text-red-500 text-sm mt-1">{errors.nextDueDate}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Technician <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.technicianId}
                onChange={(e) => handleChange("technicianId", e.target.value)}
                className={`w-full p-2 border ${
                  errors.technicianId ? "border-red-500" : "border-gray-300"
                } rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none`}
                required
                disabled={isDisabled}
                // readOnly={isReadOnly}
              >
                <option value="">Select Technician</option>
                {orgUsersData.map((user: any) => (
                  <option key={user._id || user.id} value={user._id || user.id}>
                    {`${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                      user.email ||
                      user._id ||
                      user.id}
                  </option>
                ))}
              </select>
              {errors.technicianId && (
                <p className="text-red-500 text-sm mt-1">{errors.technicianId}</p>
              )}
            </div>

            {/* Status */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">Status</label>
              <select
                value={formData.status}
                onChange={(e) =>
                  handleChange("status", e.target.value as any)
                }
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                disabled={isDisabled}
                // readOnly={isReadOnly}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Suspended">Suspended</option>
              </select>
            </div>

            {/* Priority */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) =>
                  handleChange("priority", e.target.value as any)
                }
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                disabled={isDisabled}
                // readOnly={isReadOnly as unknown as boolean | undefined}
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>

            <div className="md:col-span-2 flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                rows={3}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                disabled={isDisabled}
                readOnly={isReadOnly}
                placeholder="Enter description (optional)"
              />
            </div>
          </div>

          <div className="h-20" />
        </form>
      </div>

        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg sticky bottom-0">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={onClose}
            disabled={schedulesLoading}
            >
            Cancel
          </button>
          
        {!isReadOnly && (
          <button
            type="submit"
            form="maintenanceScheduleForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
            disabled={schedulesLoading}
          >
            {schedulesLoading ? "Saving..." : mode === "create" ? "Create" : "Update"}
          </button>
        )}
        </div>
    </Modal>
  );
};

export default MaintenanceScheduleForm;

